import { useEffect, useRef, useState } from "react";
import { X, Send } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AICoach({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState("default");
  const scrollRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("runwell_token");
    fetch(`${API}/coach/history/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setMessages(d || []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("runwell_token");
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          if (!ev.startsWith("data:")) continue;
          const payload = ev.slice(5).trim();
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, the coach is offline. Try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#F0F0EE] border-l border-neutral-300 flex flex-col z-50 shadow-xl" data-testid="ai-coach-panel">
      <div className="h-14 flex items-center justify-between px-5 border-b border-neutral-300 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#E54D2E]" />
          <div>
            <div className="font-display font-bold">AI Ops Coach</div>
            <div className="mono text-[10px] text-neutral-500 uppercase">Claude Sonnet 5 · knows your business</div>
          </div>
        </div>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="close-coach-btn"><X className="h-4 w-4" /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll p-5 space-y-4">
        {messages.length === 0 && (
          <div className="text-sm text-neutral-600 space-y-3">
            <p className="font-display text-xl text-neutral-900">Try asking:</p>
            <div className="space-y-2">
              {[
                "What should we focus on for next quarter's rocks?",
                "Our churn is climbing — what should we investigate?",
                "Draft a weekly L10 agenda for a leadership team of 6.",
                "Which KPIs should a 30-person SaaS company track?",
              ].map((q) => (
                <button key={q} onClick={() => setInput(q)} className="block w-full text-left text-xs px-3 py-2 bg-white border border-neutral-300 hover:border-neutral-900 rounded-sm" data-testid={`coach-suggest-${q.slice(0,20)}`}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-sm ${m.role === "user" ? "flex justify-end" : ""}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-800"
            }`}>{m.content}{streaming && i === messages.length - 1 && m.role === "assistant" && <span className="inline-block w-1.5 h-3.5 bg-[#E54D2E] ml-0.5 align-middle animate-pulse" />}</div>
          </div>
        ))}
      </div>

      <form onSubmit={send} className="p-3 border-t border-neutral-300 bg-white flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the coach anything…"
          className="flex-1 rounded-sm border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-neutral-900"
          data-testid="coach-input"
        />
        <Button type="submit" disabled={streaming || !input.trim()} className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm" data-testid="coach-send-btn">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
